import { supabase } from './supabase';
import axios from 'axios';
import { apiService } from './api';

/**
 * Interface para os dados da cobrança principal
 */
interface RegistroCobranca {
  codfunc: string;
  codcli: string;
  telcob: string;
  obs1: string;
  statuscob: string;
}

/**
 * Interface para os itens da cobrança
 */
interface ItemCobranca {
  codcli: string;
  codfilial: string;
  numtransvenda: string;
  duplic: string;
  prest: string;
  dtvenc: string;
  valor: string;
  codcob: string;
  valorprev: string;
  statuscob: string;
}

/**
 * Serviço para registrar cobranças na API
 */
export const cobrancaService = {
  /**
   * Obtém a URL base e token de autorização para a API
   * @param codempresa Código da empresa
   */
  async getApiConfig(codempresa: number) {
    try {
      // Busca a empresa para obter a rota da API
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('rota')
        .eq('id', codempresa)
        .single();

      if (empresaError) throw empresaError;
      if (!empresaData?.rota) throw new Error('Rota da API não encontrada para a empresa');

      // Busca a configuração da API Winthor para obter a URL base
      const { data: apiWinthorConfig, error: apiWinthorError } = await supabase
        .from('config_geral')
        .select('dados')
        .eq('nome_integracao', 'apiwinthor')
        .eq('codempresa', codempresa)
        .maybeSingle();

      // Se não encontrar configuração específica, busca a global
      let baseUrl = '';
      let apiKey = '';
      
      if (apiWinthorError || !apiWinthorConfig) {
        const { data: globalApiConfig, error: globalApiError } = await supabase
          .from('config_geral')
          .select('dados')
          .eq('nome_integracao', 'apiwinthor')
          .is('codempresa', null)
          .maybeSingle();

        if (globalApiError) throw globalApiError;
        if (!globalApiConfig?.dados?.URL) throw new Error('URL base da API não encontrada');
        
        baseUrl = globalApiConfig.dados.URL;
        // Verifica se o campo apikey está com dois pontos no final (apikey:)
        apiKey = globalApiConfig.dados['apikey:'] || globalApiConfig.dados.apikey || '';
      } else {
        baseUrl = apiWinthorConfig.dados.URL;
        // Verifica se o campo apikey está com dois pontos no final (apikey:)
        apiKey = apiWinthorConfig.dados['apikey:'] || apiWinthorConfig.dados.apikey || '';
      }
      
      // Verifica se a URL começa com http ou https
      if (!baseUrl.startsWith('http')) {
        baseUrl = `https://${baseUrl}`;
      }

      return {
        baseUrl: `${baseUrl}/sync/${empresaData.rota}`,
        authToken: apiKey
      };
    } catch (error) {
      console.error('Erro ao obter configuração da API:', error);
      throw error;
    }
  },

  /**
   * Registra uma cobrança principal
   * @param dados Dados da cobrança
   * @param codempresa Código da empresa
   */
  async registrarCobranca(dados: RegistroCobranca, codempresa: number): Promise<any> {
    try {
      console.log('Iniciando registro da cobrança principal...');
      console.log('Dados da cobrança:', dados);
      console.log('Código da empresa:', codempresa);
      
      // Prepara os dados para envio
      const dadosEnvio = {
        codfunc: dados.codfunc,
        codcli: dados.codcli,
        telcob: dados.telcob,
        obs1: dados.obs1,
        statuscob: dados.statuscob
      };
      
      console.log('Dados a serem enviados:', dadosEnvio);
      
      // Usa o apiService para fazer a requisição
      console.log('Usando apiService para enviar a requisição...');
      
      const responseData = await apiService.request(
        'SYNC',
        codempresa.toString(),
        'FINANCEIROINSERTpchistcob',
        'post',
        dadosEnvio
      );
      
      console.log('Resposta do registro da cobrança:', responseData);
      return responseData;
    } catch (error) {
      console.error('Erro ao registrar cobrança:', error);
      throw error;
    }
  },

  /**
   * Registra os itens da cobrança
   * @param itens Lista de itens da cobrança
   * @param codempresa Código da empresa
   */
  async registrarItensCobranca(itens: ItemCobranca[], codempresa: number, cobrancaId?: string): Promise<any> {
    try {
      console.log('Iniciando registro dos itens da cobrança...');
      console.log('Quantidade de itens:', itens.length);
      console.log('Primeiro item (exemplo):', itens[0]);
      console.log('Código da empresa:', codempresa);
      
      // Prepara os dados para envio
      const dadosEnvio = { data: itens };
      console.log('Estrutura dos dados a serem enviados:', {
        data: `Array com ${itens.length} itens`
      });
      
      // Usa o apiService para fazer a requisição
      console.log('Usando apiService para enviar a requisição...');
      
      const responseData = await apiService.request(
        'SYNC',
        codempresa.toString(),
        'FINANCEIROINSERTpchistcobi',
        'post',
        dadosEnvio
      );
      
      console.log('Resposta do registro dos itens:', responseData);
      return responseData;
    } catch (error) {
      console.error('Erro ao registrar itens da cobrança:', error);
      throw error;
    }
  }
};
